<template>
  <!-- 이 화면 테스트 — 시나리오 데이터에서 자동 생성되는 가이드 + 결과 기록 (2026-10-18)
       HelpPanel 의 "테스트" 탭과 App.vue 전역 서랍이 같이 쓴다. -->
  <div class="tp">
    <div v-if="qa.loading && !qa.loaded" class="tp-empty"><el-icon class="is-loading"><Loading /></el-icon> 불러오는 중…</div>

    <template v-else-if="!qa.run">
      <div class="tp-empty">
        진행 중인 테스트 회차에 배정된 것이 없습니다.<br>
        <span class="muted">테스트관리자가 회차를 시작하고 배정하면 여기에 이 화면의 시나리오가 나타납니다.</span>
      </div>
    </template>

    <template v-else>
      <div v-if="!all" class="tp-head">
        <div>
          <b>{{ qa.run.name }}</b>
          <span class="muted"> · 내 전체 {{ qa.all.length }}개 중 남은 {{ qa.pendingCount }}개</span>
        </div>
        <div>
          <el-button link size="small" @click="$router.push('/qa/my')">내 테스트 시작 화면</el-button>
          <el-button link size="small" @click="qa.refresh()">새로고침</el-button>
        </div>
      </div>

      <div v-if="showIntro" class="tp-intro">
        <div><b>처음이세요?</b> 이렇게 하면 됩니다.</div>
        <ol>
          <li>아래 절차대로 <b>이 화면에서</b> 직접 해 보세요. 입력값 칩은 누르면 복사됩니다.</li>
          <li>"확인할 것"을 <b>보이는 대로 체크</b> — 전부 맞으면 [통과]가 켜집니다.</li>
          <li>하나라도 다르면 본 대로 적고 <b>[실패]</b> — 제보가 자동으로 만들어집니다.</li>
        </ol>
        <el-button size="small" type="primary" plain @click="dismissIntro">알겠어요</el-button>
      </div>
      <div v-if="qa.hint" class="tp-hint">➜ {{ qa.hint }} <el-button link size="small" @click="qa.hint = ''">닫기</el-button></div>
      <div v-if="nextElsewhere" class="tp-nextbar">
        여기서 할 일은 끝났습니다. 다음: <b>{{ nextElsewhere.step_no }} {{ nextElsewhere.title }}</b>
        <span class="muted">({{ nextElsewhere.screen_path }})</span>
        <el-button type="primary" size="small" @click="goTo(nextElsewhere)">이동 →</el-button>
      </div>

      <div v-if="!items.length" class="tp-empty">
        이 화면에 배정된 시나리오가 없습니다.
        <div v-if="nextItem" class="tp-next">
          다음 할 일: <a @click="goTo(nextItem)">{{ nextItem.step_no }} {{ nextItem.title }}</a>
          <div class="muted">{{ nextItem.screen_path }}</div>
        </div>
      </div>

      <el-collapse v-else v-model="openIds" accordion class="tp-list">
        <el-collapse-item v-for="s in items" :key="s.id" :name="s.id">
          <template #title>
            <span class="tp-item-title" :class="{ done: s.done }">
              <span class="verdict-dot" :class="dotClass(s)"></span>
              <b>{{ s.step_no }}</b> {{ s.title }}
              <el-tag v-if="s.kind !== 'NORMAL'" size="small" effect="plain" class="kind-tag">{{ kindLabel(s.kind) }}</el-tag>
              <el-tag v-if="s.my_result && s.my_result.retest_required" size="small" type="warning" effect="plain">재테스트</el-tag>
            </span>
            <el-button v-if="all" size="small" type="primary" plain class="tp-open" @click.stop="goTo(s)">이 화면 열기 →</el-button>
          </template>

          <!-- ── 가이드 모드 ── -->
          <div class="g">
            <div class="g-path">{{ s.screen_path }} <span v-if="s.role_label" class="muted">· {{ s.role_label }}</span> <span class="muted">· v{{ s.version }}</span></div>

            <div v-if="s.handoff_in" class="g-box in">
              <div class="g-box-t">넘겨받은 것 — {{ s.handoff_in.step_no }} {{ s.handoff_in.title }}</div>
              <div v-if="s.handoff_in.verdict === 'PENDING'" class="muted">앞 단계가 아직 끝나지 않았습니다. 끝나면 실제값이 여기에 표시됩니다.</div>
              <div v-else>
                <span :class="'v-' + s.handoff_in.verdict">{{ verdictKo(s.handoff_in.verdict) }}</span>
                <span v-if="s.handoff_in.actual_text"> · {{ s.handoff_in.actual_text }}</span>
                <span class="muted"> — {{ s.handoff_in.tester }} {{ fmt(s.handoff_in.at) }}</span>
              </div>
            </div>

            <div v-if="s.procedure && s.procedure.length && s.kind !== 'LINK'" class="g-tour">
              <el-button type="warning" size="small" @click="startTour(s)">▶ 따라하기 — 화면에 ①②③ 표시</el-button>
              <span class="muted">번호 순서대로 누르면 됩니다. 끝나면 여기로 돌아와 확인할 것을 체크합니다.</span>
            </div>
            <ol v-if="s.procedure && s.procedure.length" class="g-steps">
              <li v-for="(p, i) in s.procedure" :key="i">
                <template v-if="i === 0 && p.startsWith('이동:') && s.route && all">
                  <a class="g-go" @click="goTo(s)" title="이 화면으로 이동">{{ p }}</a>
                </template>
                <template v-else>{{ p }}</template>
              </li>
            </ol>

            <div v-if="s.inputs && s.inputs.length" class="g-inputs">
              <span v-for="(inp, i) in s.inputs" :key="i" class="inp" :class="{ copied: copiedKey === s.id + ':' + i }" :title="'클릭하면 복사: ' + inp.value" @click="copy(inp.value, s.id + ':' + i)">
                <span v-if="inp.label" class="inp-l">{{ inp.label }}</span><b>{{ inp.value }}</b><span v-if="copiedKey === s.id + ':' + i" class="inp-ok">복사됨 ✓</span>
              </span>
            </div>

            <div v-if="s.expected && s.expected.length" class="g-expect">
              <div class="g-box-t">확인할 것 — 보이는 대로 체크</div>
              <el-checkbox v-for="(e, i) in s.expected" :key="i" v-model="checks[s.id][i]" class="g-chk">{{ e }}</el-checkbox>
            </div>

            <div v-if="s.images && s.images.length" class="g-ref">
              <div class="g-box-t">이렇게 보여야 함</div>
              <el-image v-for="im in s.images" :key="im.id" :src="im.url" :preview-src-list="s.images.map(x => x.url)" fit="cover" class="g-img" />
            </div>

            <div v-if="s.tips" class="g-box tips"><b>자주 막히는 곳</b> {{ s.tips }}</div>
            <div v-if="s.handoff" class="g-box out"><b>다음 단계로</b> {{ s.handoff }}</div>

            <div v-if="s.my_result" class="g-last">
              내 마지막 결과: <span :class="'v-' + s.my_result.verdict">{{ verdictKo(s.my_result.verdict) }}</span>
              #{{ s.my_result.attempt_no }} · {{ fmt(s.my_result.created_at) }}
              <span v-if="s.my_result.feedback_number"> · 제보 {{ s.my_result.feedback_number }}</span>
              <span v-if="s.my_result.actual_text"> · {{ s.my_result.actual_text }}</span>
            </div>

            <!-- ── 결과 기록 ── -->
            <div class="g-result">
              <el-input v-model="forms[s.id].actual_text" size="small" placeholder="실제값 — 다음 담당자에게 넘길 번호·금액 (예: PO-2026-0001, 25,200,000)" />
              <el-input v-model="forms[s.id].note" size="small" placeholder="비고(선택)" class="mt4" />
              <div class="g-files mt4">
                <input :ref="el => fileInputs[s.id] = el" type="file" multiple accept="image/*,.pdf,.xlsx" style="display:none" @change="onFiles(s.id, $event)">
                <el-button size="small" @click="fileInputs[s.id] && fileInputs[s.id].click()">📎 캡처 첨부</el-button>
                <span class="muted">화면 캡처(PrintScreen) 후 여기에 Ctrl+V 로 붙여넣어도 됩니다</span>
                <div v-if="forms[s.id].files.length" class="g-file-list">
                  <el-tag v-for="(f, i) in forms[s.id].files" :key="i" size="small" closable @close="forms[s.id].files.splice(i, 1)">{{ f.name }}</el-tag>
                </div>
              </div>
              <div class="g-actions mt6">
                <el-button type="success" size="small" :disabled="!allChecked(s) || saving" @click="submit(s, 'PASS')">통과</el-button>
                <el-button type="danger" size="small" :disabled="saving" @click="submit(s, 'FAIL')">실패</el-button>
                <el-button size="small" :disabled="saving" @click="submit(s, 'HOLD')">보류</el-button>
                <el-checkbox v-model="forms[s.id].create_feedback" size="small" class="ml8">실패 시 제보 자동 생성</el-checkbox>
              </div>
              <div v-if="!allChecked(s) && s.expected && s.expected.length" class="muted mt4">확인할 것을 전부 체크해야 [통과]가 켜집니다. 하나라도 다르면 [실패].</div>
            </div>
          </div>
        </el-collapse-item>
      </el-collapse>
    </template>
  </div>
</template>

<script setup>
import { computed, reactive, ref, watch, onMounted, onBeforeUnmount } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { ElMessage } from 'element-plus'
import { Loading } from '@element-plus/icons-vue'
import axiosInstance from '@/api/axios'
import { useQaStore } from '@/stores/qa'

const props = defineProps({
  // 지정하면 현재 라우트 대신 이 경로의 시나리오를 보여준다 (내 테스트 화면에서 사용)
  path: { type: String, default: '' },
  // 전체 목록 모드(내 테스트 화면): 현재 화면이 아니라 내 시나리오 전체
  all: { type: Boolean, default: false },
})

const qa = useQaStore()
const route = useRoute()
const router = useRouter()
const openIds = ref(null)
const copiedKey = ref('')
const nextElsewhere = ref(null)
const INTRO_KEY = 'qa_panel_intro_v1'
const showIntro = ref(false)
try { showIntro.value = !localStorage.getItem(INTRO_KEY) } catch (e) { showIntro.value = false }
function dismissIntro() { showIntro.value = false; try { localStorage.setItem(INTRO_KEY, '1') } catch (e) { /* */ } }
function goTo(s) { nextElsewhere.value = null; qa.openScenario(s, router) }
function startTour(s) { nextElsewhere.value = null; qa.tourScenario(s, router) }
const checks = reactive({})
const forms = reactive({})
const fileInputs = reactive({})
const saving = ref(false)

const items = computed(() => props.all ? qa.all : qa.here)
const nextItem = computed(() => qa.all.find(x => !x.done && x.stage >= 1 && x.stage <= 8) || qa.all.find(x => !x.done) || null)

function ensureState(list) {
  for (const s of list) {
    if (!checks[s.id]) checks[s.id] = (s.expected || []).map(() => false)
    if (!forms[s.id]) forms[s.id] = { actual_text: '', note: '', files: [], create_feedback: true }
  }
}
function applyFocus(v) {
  if (props.all) return false  // 내 테스트(전체 목록)는 이동을 '시키는' 쪽 — 서랍이 받는다
  if (qa.focusId && v.some(x => x.id === qa.focusId)) {
    openIds.value = qa.focusId; const id = qa.focusId; qa.focusId = null; nextElsewhere.value = null
    if (qa.pendingTourId === id) { const sc = v.find(x => x.id === id); setTimeout(() => qa.startTour(sc), 400) }
    return true
  }
  return false
}
watch(items, (v) => {
  ensureState(v)
  if (applyFocus(v)) return
  if (!openIds.value && v.length) openIds.value = (v.find(x => !x.done) || v[0]).id
}, { immediate: true })
watch(() => qa.focusId, () => { applyFocus(items.value) })

const kindLabel = (k) => ({ NORMAL: '정상', NEGATIVE: '일부러 틀리기', OUTPUT: '출력물', MOBILE: '모바일', LINK: '구간 연결', VOICE: '음성' }[k] || k)
const verdictKo = (v) => ({ PASS: '통과', FAIL: '실패', HOLD: '보류', SKIP: '미실시', PENDING: '미실시' }[v] || v)
const fmt = (v) => v ? String(v).slice(0, 16).replace('T', ' ') : ''
const dotClass = (s) => {
  if (!s.my_result) return 'pending'
  if (s.my_result.retest_required) return 'retest'
  return s.my_result.verdict.toLowerCase()
}
const allChecked = (s) => !s.expected || !s.expected.length || (checks[s.id] || []).every(Boolean)

async function copy(text, key) {
  try { await navigator.clipboard.writeText(text) } catch (e) { /* 클립보드 미지원이면 선택만 */ }
  copiedKey.value = key
  setTimeout(() => { if (copiedKey.value === key) copiedKey.value = '' }, 1200)
}
function onFiles(id, ev) {
  for (const f of ev.target.files || []) forms[id].files.push(f)
  ev.target.value = ''
}
function onPaste(ev) {
  const id = openIds.value
  if (!id || !forms[id]) return
  for (const it of ev.clipboardData?.items || []) {
    if (it.type.startsWith('image/')) {
      const f = it.getAsFile()
      if (f) forms[id].files.push(new File([f], `capture_${Date.now()}.png`, { type: f.type }))
    }
  }
}
onMounted(() => window.addEventListener('paste', onPaste))
onBeforeUnmount(() => window.removeEventListener('paste', onPaste))

async function submit(s, verdict) {
  const f = forms[s.id]
  if (verdict === 'FAIL' && !f.actual_text) { ElMessage.warning('실패는 "실제값/실제 동작"을 적어 주세요 — 개발자가 그걸 보고 고칩니다.'); return }
  saving.value = true
  try {
    const fd = new FormData()
    fd.append('run', qa.run.id); fd.append('scenario', s.id); fd.append('verdict', verdict)
    fd.append('actual_text', f.actual_text || ''); fd.append('note', f.note || '')
    fd.append('checked', JSON.stringify((checks[s.id] || []).map((v, i) => v ? i : -1).filter(i => i >= 0)))
    fd.append('create_feedback', f.create_feedback ? 'true' : 'false')
    for (const file of f.files) fd.append('files', file)
    const res = await axiosInstance.post('qa/results/', fd, { headers: { 'Content-Type': 'multipart/form-data' } })
    const r = res.data
    ElMessage.success(verdict === 'PASS' ? `${s.step_no} 통과 기록됨` : verdict === 'FAIL' ? `${s.step_no} 실패 기록됨${r.feedback_number ? ' · 제보 ' + r.feedback_number : ''}` : `${s.step_no} 보류 기록됨`)
    f.files = []; f.note = ''
    await qa.refresh()
    const nxt = items.value.find(x => !x.done && x.id !== s.id)
    if (nxt) { openIds.value = nxt.id; nextElsewhere.value = null }
    else if (!props.all) {
      // 다음 할 일 — 실제 화면 단계(1~8) 우선, 공통 데이터(0-x)·구간 연결(L-x)은 맨 뒤
      const rest = qa.all.filter(x => !x.done && x.id !== s.id)
      nextElsewhere.value = rest.find(x => x.stage >= 1 && x.stage <= 8) || rest[0] || null
    }
  } catch (e) {
    ElMessage.error(e?.response?.data?.detail || JSON.stringify(e?.response?.data || '저장 실패'))
  } finally { saving.value = false }
}

// 라우트 바뀌면 다시
watch(() => props.path || route.fullPath, () => { qa.fetch(props.path || route.path) }, { immediate: true })
</script>

<style scoped>
.tp { font-size: 13px; }
.tp-empty { padding: 14px 6px; color: #666; line-height: 1.6; }
.tp-next { margin-top: 8px; }
.tp-intro { background: #ecf5ff; border: 1px solid #b3d8ff; border-radius: 6px; padding: 8px 10px; margin-bottom: 8px; line-height: 1.5; }
.tp-intro ol { margin: 4px 0 6px 18px; padding: 0; }
.tp-hint { background: #fdf6ec; border-radius: 4px; padding: 6px 8px; margin-bottom: 8px; color: #8a5a00; }
.tp-nextbar { background: #f0f9eb; border: 1px solid #c2e7b0; border-radius: 6px; padding: 8px 10px; margin-bottom: 8px; display: flex; gap: 6px; align-items: center; flex-wrap: wrap; }
.tp-open { margin-left: auto; margin-right: 8px; }
.g-go { color: #1a4d8f; text-decoration: underline; cursor: pointer; }
.inp.copied { border-color: #67c23a; background: #f0f9eb; }
.inp-ok { color: #67c23a; font-size: 11px; margin-left: 4px; } .tp-next a { color: #1a4d8f; cursor: pointer; text-decoration: underline; }
.tp-head { display: flex; justify-content: space-between; align-items: center; margin: 2px 0 6px; }
.muted { color: #909399; font-size: 12px; }
.tp-item-title { display: inline-flex; align-items: center; gap: 6px; }
.tp-item-title.done { color: #8a8a8a; }
.verdict-dot { width: 9px; height: 9px; border-radius: 50%; background: #dcdfe6; display: inline-block; }
.verdict-dot.pass { background: #67c23a } .verdict-dot.fail { background: #f56c6c } .verdict-dot.hold { background: #e6a23c } .verdict-dot.retest { background: #e6a23c; box-shadow: 0 0 0 2px #fdf6ec }
.kind-tag { margin-left: 2px; }
.g { padding: 0 2px 4px; }
.g-path { color: #555; margin-bottom: 6px; }
.g-box { border-radius: 4px; padding: 6px 8px; margin: 6px 0; line-height: 1.5; }
.g-box.in { background: #ecf5ff; } .g-box.out { background: #f0f9eb; } .g-box.tips { background: #fdf6ec; }
.g-box-t { font-weight: 600; margin-bottom: 3px; }
.g-steps { margin: 4px 0 6px 18px; padding: 0; line-height: 1.7; }
.g-tour { margin: 4px 0 8px; display: flex; gap: 8px; align-items: center; flex-wrap: wrap; }
.g-inputs { margin: 4px 0 6px; }
.inp { display: inline-block; border: 1px solid #dcdfe6; background: #fafafa; border-radius: 3px; padding: 1px 6px; margin: 0 4px 4px 0; cursor: pointer; }
.inp:hover { border-color: #409eff; background: #ecf5ff; }
.inp-l { color: #777; margin-right: 4px; }
.g-expect { margin: 6px 0; }
.g-chk { display: flex; margin: 2px 0; white-space: normal; height: auto; align-items: flex-start; }
.g-chk :deep(.el-checkbox__label) { white-space: normal; line-height: 1.4; }
.g-ref { margin: 6px 0; }
.g-img { width: 96px; height: 64px; margin: 2px 4px 0 0; border: 1px solid #ddd; border-radius: 3px; }
.g-last { margin: 6px 0; color: #555; }
.g-result { border-top: 1px dashed #dcdfe6; margin-top: 8px; padding-top: 8px; }
.g-file-list { margin-top: 4px; }
.g-actions { display: flex; align-items: center; flex-wrap: wrap; gap: 4px; }
.mt4 { margin-top: 4px } .mt6 { margin-top: 6px } .ml8 { margin-left: 8px }
.v-PASS { color: #67c23a; font-weight: 600 } .v-FAIL { color: #f56c6c; font-weight: 600 } .v-HOLD, .v-SKIP, .v-PENDING { color: #e6a23c; font-weight: 600 }
</style>
